import { useEffect } from 'react'
import { Youtube } from 'lucide-react'
import '../DocumentPage.css'

const ExampleFragments = () => {
  useEffect(() => {
    const headings = document.querySelectorAll('.doc-content h2, .doc-content h3')
    headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = heading.textContent.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
      }
    })
  }, [])

  return (
    <div className="doc-page">
      <div className="doc-content">
        <h1>YouTube: Example in Fragments</h1>
        <p className="lead">
          Learn how to use the Variable Modding System with game fragments through a practical example.
        </p>

        <h2>Watch the Tutorial</h2>
        <div style={{
          position: 'relative',
          paddingBottom: '56.25%',
          height: 0,
          overflow: 'hidden',
          maxWidth: '100%',
          backgroundColor: 'var(--color-bg-elevated)',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          {/* YouTube video will be embedded here */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}>
            <Youtube size={64} style={{ color: 'var(--color-brand)', marginBottom: '1rem' }} />
            <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>
              YouTube video embed will be added here
            </p>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              (Video URL to be provided)
            </p>
          </div>
          {/* Replace the above div with this when you have the URL:
          <iframe
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
            src="https://www.youtube.com/embed/YOUR_VIDEO_ID"
            title="Variable Modding System - Fragment Example"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
          */}
        </div>

        <h2>Video Contents</h2>
        <h3>Topics Covered</h3>
        <ul>
          <li>Understanding game fragments in Unreal Engine</li>
          <li>Setting up fragment-specific variables</li>
          <li>Managing fragment data with CSV files</li>
          <li>Dynamic fragment loading based on variables</li>
          <li>Performance optimization for fragment systems</li>
          <li>Real-world use case implementation</li>
        </ul>

        <h3>Timestamps</h3>
        <ul>
          <li><strong>0:00</strong> - Introduction to fragments</li>
          <li><strong>2:45</strong> - Project setup</li>
          <li><strong>6:20</strong> - Creating fragment variables</li>
          <li><strong>11:00</strong> - CSV configuration for fragments</li>
          <li><strong>15:30</strong> - Blueprint integration</li>
          <li><strong>20:15</strong> - Testing and debugging</li>
          <li><strong>24:00</strong> - Optimization tips</li>
        </ul>

        <h2>Example Project</h2>
        <p>This tutorial includes a complete example project demonstrating:</p>
        <ul>
          <li>Fragment-based level streaming</li>
          <li>Dynamic content loading</li>
          <li>Variable-driven fragment selection</li>
          <li>Performance profiling</li>
        </ul>

        <h2>Download Resources</h2>
        <p>Get the example project files:</p>
        <ul>
          <li>Complete Unreal Engine project</li>
          <li>Fragment CSV configurations</li>
          <li>Blueprint examples</li>
          <li>Documentation and comments</li>
        </ul>

        <h2>Prerequisites</h2>
        <p>Before watching this tutorial, make sure you're familiar with:</p>
        <ul>
          <li>Basic Variable Modding System usage</li>
          <li>Unreal Engine level streaming</li>
          <li>Blueprint fundamentals</li>
        </ul>

        <h2>Related Content</h2>
        <ul>
          <li><a href="#/video-tutorials/getting-started">Getting Started Tutorial</a></li>
          <li><a href="#/core-concepts/csv-generation">CSV Generation Guide</a></li>
          <li><a href="#/advanced">Advanced Documentation</a></li>
        </ul>

        <h2>Community Support</h2>
        <p>
          Discuss this example and share your own fragment implementations in our{' '}
          <a href="https://discord.gg/WbbBfkNaxt" target="_blank" rel="noopener noreferrer">
            Discord community
          </a>.
        </p>
      </div>
    </div>
  )
}

export default ExampleFragments
